import { useEffect, useEffectEvent, useState } from 'react';

type RequestOptionsType<T> = {
  onSuccess?: (data: T) => void;
  onError?: (error: any) => void;
  onFinally?: () => void;
  enabled?: boolean;
  initialData?: any;
  timeout?: number;
};

// Overload the useRequest function based on whether the request is a single function or an array
export function useRequest<T>(
  request: (params?: any) => Promise<T>,
  requestOptions?: RequestOptionsType<T>,
): {
  data: T | null;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  executeRequest: (params?: any) => Promise<T | void>;
  clearError: () => void;
};

export function useRequest<T>(
  request: Array<(params?: any) => Promise<T>>,
  requestOptions?: RequestOptionsType<T[]>,
): {
  data: T[] | null;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  executeRequest: (params?: any) => Promise<T[] | void>;
  clearError: () => void;
};

export function useRequest<T>(
  request: ((params?: any) => Promise<T>) | Array<(params?: any) => Promise<T>>,
  requestOptions?: RequestOptionsType<T> | RequestOptionsType<T[]>,
) {
  const { enabled, initialData = {}, timeout, ...options } = requestOptions || {};

  const [isLoading, setIsLoading] = useState(Boolean(enabled));
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState<T | T[] | null>(null);

  const clearError = () => setIsError(false);

  const raceWithTimeout = <R>(promise: Promise<R>, timeoutDuration: number): Promise<R> => {
    return new Promise<R>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Request timed out after ${timeoutDuration / 1000.0} seconds`));
      }, timeoutDuration);

      promise
        .then((result) => {
          clearTimeout(timeoutId);
          resolve(result);
        })
        .catch((error) => {
          clearTimeout(timeoutId);
          reject(error);
        });
    });
  };

  const fetch = async (params?: any): Promise<T | T[] | void> => {
    try {
      setIsLoading(true);
      setIsSuccess(false);
      setIsError(false);

      let response: T | T[];

      if (Array.isArray(request)) {
        const requests = request.map((r) => r(params));
        const allRequestsPromise = Promise.all(requests);

        response = timeout ? await raceWithTimeout(allRequestsPromise, timeout) : await allRequestsPromise;

        setData(response);
        setIsSuccess(true);

        if (options.onSuccess) {
          (options as RequestOptionsType<T[]>).onSuccess(response);
        }

        return response;
      } else {
        const requestPromise = request(params);

        response = timeout ? await raceWithTimeout(requestPromise, timeout) : await requestPromise;

        setData(response);
        setIsSuccess(true);

        if (options.onSuccess) {
          (options as RequestOptionsType<T>).onSuccess(response);
        }

        return response;
      }
    } catch (error) {
      setIsError(true);

      if (options.onError) {
        options.onError(error);
      }
    } finally {
      setIsLoading(false);

      if (options.onFinally) {
        options.onFinally();
      }
    }
  };

  const executeRequest = (params?: any) => {
    if (isLoading) return;

    return fetch(params);
  };

  const onInit = useEffectEvent(() => {
    if (!enabled) return;

    fetch(initialData);
  });

  useEffect(() => {
    onInit();
  }, [enabled]);

  return {
    data,
    isLoading,
    isSuccess,
    isError,
    executeRequest,
    clearError,
  };
}
